import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';


// ─── Animations ───────────────────────────────────────────

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const floatUp = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

// ─── Layout ───────────────────────────────────────────────

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${props => props.theme.colors.bgPrimary};
  overflow-x: hidden;
`;

const Section = styled.section`
  width: 100%;
  padding: 80px 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 48px 16px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  text-align: center;
  margin-bottom: 48px;
  letter-spacing: 0.5px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.3rem;
    margin-bottom: 32px;
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.mono};
  margin-bottom: 12px;
`;

// ─── Hero Section ─────────────────────────────────────────

const HeroSection = styled(Section)`
  position: relative;
  padding: 120px 24px 100px;
  text-align: center;
  background: radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
              radial-gradient(ellipse at 30% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
              ${props => props.theme.colors.bgPrimary};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 80px 16px 60px;
  }
`;

const HeroGlow = styled.div`
  position: absolute;
  top: -120px;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(99, 102, 241, 0.12) 0%, transparent 70%);
  pointer-events: none;
  animation: ${pulseGlow} 4s ease-in-out infinite;
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.mono};
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 24px;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 2.6rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  line-height: 1.3;
  margin-bottom: 20px;
  letter-spacing: 0.5px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.05rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.8;
  max-width: 640px;
  margin: 0 auto 40px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.92rem;
  }
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: ${props => props.theme.colors.gradient};
  color: #ffffff;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  font-size: 0.95rem;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const OutlinedButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  font-size: 0.95rem;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    background: rgba(99, 102, 241, 0.05);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ─── Features Section ─────────────────────────────────────

const FeaturesSection = styled(Section)`
  background: ${props => props.theme.colors.bgSecondary};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glow};
    transform: translateY(-4px);
  }
`;

const FeatureIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: rgba(99, 102, 241, 0.1);
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 10px;
`;

const FeatureDesc = styled.p`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.7;
`;

// ─── Demo Visualization Section ───────────────────────────

const DemoSection = styled(Section)`
  background: ${props => props.theme.colors.bgPrimary};
  position: relative;
`;

const DemoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 32px;
  }
`;

const DemoChartWrap = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const FloatingSvg = styled.svg`
  animation: ${floatUp} 4s ease-in-out infinite;
`;

const ChartGlow = styled.div`
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
  animation: ${pulseGlow} 3s ease-in-out infinite;
  pointer-events: none;
`;

const DemoText = styled.div`
  flex: 1;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const DemoTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const DemoDesc = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.8;
  margin-bottom: 24px;
`;

const AxisList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const AxisTag = styled.span`
  display: inline-block;
  padding: 6px 14px;
  font-size: 0.78rem;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: ${props => props.theme.borderRadius.full};
  font-family: ${props => props.theme.fonts.mono};
`;

// ─── How It Works Section ─────────────────────────────────

const StepsSection = styled(Section)`
  background: ${props => props.theme.colors.bgSecondary};
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  position: relative;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const StepConnector = styled.div`
  position: absolute;
  top: 40px;
  left: calc(33.33% - 16px);
  width: calc(33.33% + 32px);
  height: 2px;
  background: ${props => props.theme.colors.gradient};
  opacity: 0.3;

  &:last-of-type {
    left: calc(66.66% - 16px);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const StepCard = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const StepNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.colors.gradient};
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 700;
  font-family: ${props => props.theme.fonts.mono};
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(99, 102, 241, 0.3);
`;

const StepTitle = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 10px;
`;

const StepDesc = styled.p`
  font-size: 0.83rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.7;
`;

// ─── Bottom CTA ───────────────────────────────────────────

const CTASection = styled(Section)`
  background: radial-gradient(ellipse at 50% 100%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
              ${props => props.theme.colors.bgPrimary};
  text-align: center;
  padding: 100px 24px;
`;

const CTATitle = styled(motion.h2)`
  font-size: 2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const CTADesc = styled(motion.p)`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 36px;
  line-height: 1.7;
`;

const CTAButtonLarge = styled(PrimaryButton)`
  padding: 16px 48px;
  font-size: 1.05rem;
`;

// ─── SVG Icons ────────────────────────────────────────────

const OrgAnalysisIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <line x1="10" y1="6.5" x2="14" y2="6.5" opacity="0.4" />
    <line x1="6.5" y1="10" x2="6.5" y2="14" opacity="0.4" />
  </svg>
);

const TeamOptIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3 L12 12 L19.8 16.5" />
    <path d="M12 12 L4.2 16.5" opacity="0.5" />
    <path d="M12 12 L12 3" />
    <circle cx="12" cy="3" r="1.5" fill="#8B5CF6" />
    <circle cx="19.8" cy="16.5" r="1.5" fill="#8B5CF6" />
    <circle cx="4.2" cy="16.5" r="1.5" fill="#8B5CF6" />
  </svg>
);

const PersonalIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EC4899" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20 C4 16 8 14 12 14 C16 14 20 16 20 20" />
    <path d="M16 4 L18 6 L16 8" opacity="0.5" />
    <path d="M20 6 L18 6" opacity="0.5" />
  </svg>
);

// ─── Demo Radar Chart SVG ─────────────────────────────────

const DemoRadarChart = () => {
  const cx = 150;
  const cy = 150;
  const size = 150;
  const levels = 4;
  const axes = 5;
  const demoValues = [0.85, 0.65, 0.78, 0.55, 0.92];

  const angleStep = (Math.PI * 2) / axes;
  const startAngle = -Math.PI / 2;

  const getPoint = (axisIndex, value) => {
    const angle = startAngle + axisIndex * angleStep;
    const r = size * value;
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle)
    };
  };

  const gridLines = [];
  for (let level = 1; level <= levels; level++) {
    const r = (size / levels) * level;
    const points = [];
    for (let i = 0; i < axes; i++) {
      const angle = startAngle + i * angleStep;
      points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    gridLines.push(
      <polygon
        key={`grid-${level}`}
        points={points.join(' ')}
        fill="none"
        stroke="rgba(99, 102, 241, 0.12)"
        strokeWidth="1"
      />
    );
  }

  const axisLines = [];
  for (let i = 0; i < axes; i++) {
    const point = getPoint(i, 1);
    axisLines.push(
      <line
        key={`axis-${i}`}
        x1={cx}
        y1={cy}
        x2={point.x}
        y2={point.y}
        stroke="rgba(99, 102, 241, 0.1)"
        strokeWidth="1"
      />
    );
  }

  const dataPoints = demoValues.map((v, i) => getPoint(i, v));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + ' Z';

  return (
    <FloatingSvg width="300" height="300" viewBox="0 0 300 300">
      <defs>
        <linearGradient id="radarFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.25" />
          <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#EC4899" stopOpacity="0.15" />
        </linearGradient>
        <linearGradient id="radarStroke" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366F1" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#EC4899" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {gridLines}
      {axisLines}

      <path
        d={dataPath}
        fill="url(#radarFill)"
        stroke="url(#radarStroke)"
        strokeWidth="2"
        filter="url(#glow)"
      />

      {dataPoints.map((p, i) => (
        <circle
          key={`dot-${i}`}
          cx={p.x}
          cy={p.y}
          r="4"
          fill={['#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899'][i]}
          stroke="#0a0a14"
          strokeWidth="2"
        />
      ))}
    </FloatingSvg>
  );
};

// ─── My Organizations Banner ─────────────────────────────

const MyOrgsSection = styled.div`
  max-width: 640px;
  margin: -40px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 2;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: -20px;
    padding: 0 16px;
  }
`;

const MyOrgsCard = styled.div`
  background: ${props => props.theme.colors.bgSecondary};
  border: 1px solid ${props => props.theme.colors.primary}40;
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 24px;
  box-shadow: 0 4px 24px rgba(99, 102, 241, 0.08);
`;

const MyOrgsTitle = styled.h3`
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 16px 0;
`;

const OrgItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  margin-bottom: 8px;
  text-align: left;

  &:last-child { margin-bottom: 0; }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(99, 102, 241, 0.05);
  }
`;

const OrgName = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const OrgMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: ${props => props.theme.colors.textMuted};
`;

const OrgRoleBadge = styled.span`
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  ${props => props.$admin
    ? `background: rgba(236, 72, 153, 0.15); color: #EC4899; border: 1px solid #EC4899;`
    : `background: ${props.theme.colors.bgTertiary}; color: ${props.theme.colors.textSecondary}; border: 1px solid ${props.theme.colors.border};`
  }
`;

// ─── Component ────────────────────────────────────────────

const CompanyLandingPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [myCompanies, setMyCompanies] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      companyService.getMyCompanies()
        .then(data => setMyCompanies(data.companies || []))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  const features = [
    {
      icon: <OrgAnalysisIcon />,
      title: t('組織分析', 'Organization Analysis'),
      desc: t(
        '組織全体のタイプ分布と軸バランスを一目で把握',
        'Grasp your organization type distribution and axis balance at a glance'
      )
    },
    {
      icon: <TeamOptIcon />,
      title: t('チーム最適化', 'Team Optimization'),
      desc: t(
        'チームの構造的な強み・弱みを5軸で可視化',
        'Visualize team structural strengths and weaknesses across 5 axes'
      )
    },
    {
      icon: <PersonalIcon />,
      title: t('個人の活用', 'Individual Leverage'),
      desc: t(
        'メンバー一人ひとりのSTRUCT CODEを組織に活かす',
        "Leverage each member's STRUCT CODE for the organization"
      )
    }
  ];

  const steps = [
    {
      num: 1,
      title: t('組織を作成', 'Create Organization'),
      desc: t(
        '組織名と基本情報を入力して、分析環境を構築します。招待コードが自動生成されます。',
        'Enter your organization name and basic info to set up your analysis environment. An invite code is generated automatically.'
      )
    },
    {
      num: 2,
      title: t('メンバーが診断', 'Members Take Diagnosis'),
      desc: t(
        '招待コードを共有し、メンバーそれぞれがSTRUCT CODE診断を実施。統計的手法で行動特性を分析します。',
        'Share the invite code so each member takes their STRUCT CODE diagnosis. Behavioral traits are analyzed using statistical methods.'
      )
    },
    {
      num: 3,
      title: t('分析結果を活用', 'Leverage Analysis Results'),
      desc: t(
        '組織全体のタイプ分布・5軸バランスを可視化。チーム編成や適材適所の意思決定に活用できます。',
        'Visualize organization-wide type distribution and 5-axis balance. Use insights for team composition and talent placement decisions.'
      )
    }
  ];

  // 表示名は 2026-05-18 確定のもの (Pentagon.js が正)。
  // ここだけ旧名 (起動軸/判断軸/選択軸/共鳴軸/自覚軸) が残っていた。
  const axisLabels = [
    t('行動特性', 'Behavioral'),
    t('判断基準', 'Judgment'),
    t('価値観', 'Values'),
    t('共感性', 'Empathy'),
    t('自己認識', 'Self-Awareness')
  ];

  return (
    <Page>
      {/* ── Hero ── */}
      <HeroSection>
        <HeroGlow />
        <Inner>
          <HeroBadge
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            STRUCT CODE for Teams
          </HeroBadge>

          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t(
              '組織の構造を、コードで読み解く',
              'Decode Your Organization with STRUCT CODE'
            )}
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t(
              '統計学に基づく5軸フレームワークで、チームの強み・弱みを可視化し、組織力を最大化する',
              'Visualize team strengths and weaknesses with our statistics-based 5-axis framework to maximize organizational power'
            )}
          </HeroSubtitle>

          <HeroButtons
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <PrimaryButton onClick={() => navigate('/company/create')}>
              {t('組織を作成する', 'Create Organization')}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </PrimaryButton>
            <OutlinedButton onClick={() => navigate('/company/join')}>
              {t('招待コードで参加', 'Join with Invite Code')}
            </OutlinedButton>
          </HeroButtons>
        </Inner>
      </HeroSection>

      {/* ── My Organizations ── */}
      {isAuthenticated && myCompanies.length > 0 && (
        <MyOrgsSection>
          <MyOrgsCard>
            <MyOrgsTitle>{t('参加中の組織', 'My Organizations')}</MyOrgsTitle>
            {myCompanies.map(org => (
              <OrgItem key={org.id} onClick={() => navigate(`/company/${org.slug}`)}>
                <OrgName>{org.name}</OrgName>
                <OrgMeta>
                  <OrgRoleBadge $admin={org.role === 'admin'}>
                    {org.role === 'admin' ? 'Admin' : 'Member'}
                  </OrgRoleBadge>
                  <span>{org.member_count} {t('名', 'members')}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </OrgMeta>
              </OrgItem>
            ))}
          </MyOrgsCard>
        </MyOrgsSection>
      )}

      {/* ── Features ── */}
      <FeaturesSection>
        <Inner>
          <div style={{ textAlign: 'center' }}>
            <SectionLabel>Features</SectionLabel>
          </div>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t(
              '統計学に基づく行動特性分析で、組織を構造的に理解する',
              'Understand your organization structurally through statistics-based behavioral analysis'
            )}
          </SectionTitle>

          <FeaturesGrid>
            {features.map((f, i) => (
              <FeatureCard
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <FeatureIconWrap>{f.icon}</FeatureIconWrap>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureDesc>{f.desc}</FeatureDesc>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Inner>
      </FeaturesSection>

      {/* ── Demo Visualization ── */}
      <DemoSection>
        <Inner>
          <DemoContainer>
            <DemoChartWrap
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ChartGlow />
              <DemoRadarChart />
            </DemoChartWrap>

            <DemoText>
              <SectionLabel>Visualization</SectionLabel>
              <DemoTitle>
                {t(
                  '5軸レーダーチャートで構造を可視化',
                  'Visualize Structure with 5-Axis Radar Chart'
                )}
              </DemoTitle>
              <DemoDesc>
                {t(
                  '統計的手法による構造分析で、個人とチームの行動特性を5つの軸で定量化。組織全体の傾向を直感的に把握し、データに基づく意思決定を支援します。',
                  'Quantify individual and team behavioral traits across 5 axes through statistical structural analysis. Intuitively grasp organization-wide trends and support data-driven decision making.'
                )}
              </DemoDesc>
              <AxisList>
                {axisLabels.map((label, i) => (
                  <AxisTag key={i}>{label}</AxisTag>
                ))}
              </AxisList>
            </DemoText>
          </DemoContainer>
        </Inner>
      </DemoSection>

      {/* ── How It Works ── */}
      <StepsSection>
        <Inner>
          <div style={{ textAlign: 'center' }}>
            <SectionLabel>How It Works</SectionLabel>
          </div>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('3ステップで始める', 'Get Started in 3 Steps')}
          </SectionTitle>

          <StepsGrid>
            <StepConnector />
            <StepConnector style={{ left: 'calc(66.66% - 16px)' }} />

            {steps.map((step, i) => (
              <StepCard
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <StepNumber>{step.num}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDesc>{step.desc}</StepDesc>
              </StepCard>
            ))}
          </StepsGrid>
        </Inner>
      </StepsSection>

      {/* ── Bottom CTA ── */}
      <CTASection>
        <Inner>
          <CTATitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t(
              '組織の力を、構造から最大化する',
              'Maximize Organizational Power Through Structure'
            )}
          </CTATitle>
          <CTADesc
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {t(
              '統計学に基づく行動特性分析で、チームビルディングと適材適所を実現',
              'Achieve optimal team building and talent placement through statistics-based behavioral analysis'
            )}
          </CTADesc>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <CTAButtonLarge onClick={() => navigate('/company/create')}>
              {t('今すぐ始める', 'Get Started Now')}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </CTAButtonLarge>
          </motion.div>
        </Inner>
      </CTASection>
    </Page>
  );
};

export default CompanyLandingPage;
